import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";

type FormKey =
  | "username" | "email" | "first_name" | "last_name"
  | "phone_number" | "role" | "password" | "password2";

const initialForm: Record<FormKey, string> = {
  username: "", email: "", first_name: "", last_name: "",
  phone_number: "", role: "property_manager", password: "", password2: "",
};

export default function RegisterPage() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const navigate = useNavigate();

  const set = (key: FormKey) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.password2) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register/", form);
      navigate("/login");
    } catch (err: any) {
      const d = err.response?.data;
      setError(
        Object.entries(d || {})
          .map(([k, v]) => `${k}: ${(v as string[]).join(", ")}`)
          .join(" | ") || "Registration failed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      {/* ── Left panel ── */}
      <div style={styles.left}>
        <div style={styles.leftInner}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg viewBox="0 0 24 24" style={{ width: 26, height: 26, fill: "white" }}>
                <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z" />
              </svg>
            </div>
            <span style={styles.logoText}>Kisiwa Waters</span>
          </div>

          <div style={styles.heroBlock}>
            <h1 style={styles.heroTitle}>
              Join Kenya's<br />
              <span style={styles.heroAccent}>smartest water<br />billing platform.</span>
            </h1>
            <p style={styles.heroSub}>
              Register as a property manager or tenant and start managing water billing intelligently.
            </p>
          </div>

          {/* Feature highlights */}
          <div style={styles.featureList}>
            {[
              { icon: "💧", text: "Real-time IoT meter monitoring" },
              { icon: "📱", text: "M-Pesa STK push payments" },
              { icon: "🔔", text: "Automated SMS alerts & leakage detection" },
              { icon: "🏘️", text: "Multi-property dashboard" },
            ].map((f) => (
              <div key={f.text} style={styles.featureItem}>
                <span style={styles.featureEmoji}>{f.icon}</span>
                <span style={styles.featureText}>{f.text}</span>
              </div>
            ))}
          </div>

          <div style={{ ...styles.circle, width: 300, height: 300, top: -60, right: -60, opacity: 0.1 }} />
          <div style={{ ...styles.circle, width: 180, height: 180, bottom: 40, left: -40, opacity: 0.08 }} />
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={styles.right}>
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <div style={styles.formIconWrap}>
              <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "#0ea5e9" }}>
                <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z" />
              </svg>
            </div>
            <h2 style={styles.formTitle}>Create account</h2>
            <p style={styles.formSub}>Fill in your details to get started</p>
          </div>

          {error && (
            <div style={styles.errorBox}>
              <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#dc2626", flexShrink: 0 }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Name row */}
            <div style={styles.twoCol}>
              <Field label="First Name" icon="person">
                <input style={styles.input} type="text" value={form.first_name}
                  onChange={set("first_name")} placeholder="First name" required />
              </Field>
              <Field label="Last Name" icon="person">
                <input style={styles.input} type="text" value={form.last_name}
                  onChange={set("last_name")} placeholder="Last name" required />
              </Field>
            </div>

            {/* Username */}
            <Field label="Username" icon="at">
              <input style={styles.input} type="text" value={form.username}
                onChange={set("username")} placeholder="Choose a username" required />
            </Field>

            {/* Email */}
            <Field label="Email address" icon="email">
              <input style={styles.input} type="email" value={form.email}
                onChange={set("email")} placeholder="you@example.com" required />
            </Field>

            {/* Phone */}
            <Field label="Phone number" icon="phone">
              <input style={styles.input} type="tel" value={form.phone_number}
                onChange={set("phone_number")} placeholder="07XXXXXXXX" required />
            </Field>

            {/* Role */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>I am registering as</label>
              <div style={styles.roleRow}>
                {(["property_manager", "tenant"] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, role: r }))}
                    style={form.role === r ? styles.roleActive : styles.roleInactive}
                  >
                    <span>{r === "property_manager" ? "🏘️" : "🏠"}</span>
                    <span>{r === "property_manager" ? "Property Manager" : "Tenant"}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Password */}
            <div style={styles.twoCol}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrap}>
                  <LockIcon />
                  <input
                    style={{ ...styles.input, paddingRight: 44 }}
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={set("password")}
                    placeholder="Password"
                    required
                  />
                  <EyeBtn show={showPwd} toggle={() => setShowPwd((v) => !v)} />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Confirm password</label>
                <div style={styles.inputWrap}>
                  <LockIcon />
                  <input
                    style={{ ...styles.input, paddingRight: 44 }}
                    type={showPwd2 ? "text" : "password"}
                    value={form.password2}
                    onChange={set("password2")}
                    placeholder="Confirm"
                    required
                  />
                  <EyeBtn show={showPwd2} toggle={() => setShowPwd2((v) => !v)} />
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading} style={loading ? styles.btnDisabled : styles.btn}>
              {loading ? (
                <span style={styles.btnInner}><span style={styles.spinner} /> Creating account…</span>
              ) : (
                <span style={styles.btnInner}>
                  Create Account
                  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "white" }}>
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                  </svg>
                </span>
              )}
            </button>
          </form>

          <p style={styles.switchText}>
            Already have an account?{" "}
            <Link to="/login" style={styles.switchLink}>Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────────── */
function Field({ label, icon, children }: { label: string; icon: string; children: React.ReactNode }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label}</label>
      <div style={styles.inputWrap}>
        <FieldIcon type={icon} />
        {children}
      </div>
    </div>
  );
}

function FieldIcon({ type }: { type: string }) {
  const paths: Record<string, string> = {
    person: "M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z",
    at: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10h5v-2h-5c-4.34 0-8-3.66-8-8s3.66-8 8-8 8 3.66 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z",
    email: "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
    phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  };
  return (
    <svg viewBox="0 0 24 24" style={styles.inputIcon as React.CSSProperties}>
      <path d={paths[type] || ""} />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.inputIcon as React.CSSProperties}>
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
    </svg>
  );
}

function EyeBtn({ show, toggle }: { show: boolean; toggle: () => void }) {
  return (
    <button type="button" onClick={toggle} style={styles.eyeBtn as React.CSSProperties} tabIndex={-1}>
      {show ? (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#94a3b8" }}>
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#94a3b8" }}>
          <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
        </svg>
      )}
    </button>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────── */
const grad = "linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)";

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    minHeight: "100vh",
    fontFamily: "'Afacad Flux', sans-serif",
  },
  left: {
    flex: "0 0 42%",
    background: "linear-gradient(145deg, #0f172a 0%, #0c4a6e 60%, #0e7490 100%)",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
  },
  leftInner: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "48px 48px",
    width: "100%",
    gap: 40,
  },
  logo: { display: "flex", alignItems: "center", gap: 12 },
  logoIcon: {
    width: 44, height: 44, borderRadius: 12, background: grad,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 16px rgba(14,165,233,.4)",
  },
  logoText: { fontWeight: 800, fontSize: "1.2rem", color: "white", letterSpacing: "-0.02em" },
  heroBlock: {},
  heroTitle: {
    fontSize: "clamp(1.8rem, 3vw, 2.6rem)",
    fontWeight: 900, color: "white", lineHeight: 1.1,
    letterSpacing: "-0.03em", margin: 0, marginBottom: 16,
  },
  heroAccent: {
    background: grad,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  heroSub: {
    fontSize: "0.95rem", color: "rgba(186,230,253,0.85)", lineHeight: 1.7, maxWidth: 320, margin: 0,
  },
  featureList: { display: "flex", flexDirection: "column", gap: 12 },
  featureItem: {
    display: "flex", alignItems: "center", gap: 12,
    background: "rgba(255,255,255,0.07)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12, padding: "10px 16px",
  },
  featureEmoji: { fontSize: "1.1rem" },
  featureText: { fontSize: "0.875rem", color: "rgba(226,232,240,0.9)", fontWeight: 500 },
  circle: {
    position: "absolute", borderRadius: "50%",
    background: "radial-gradient(circle, #0ea5e9 0%, transparent 70%)",
    pointerEvents: "none",
  },

  /* RIGHT */
  right: {
    flex: 1, background: "#f8fafc",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "32px 24px", overflowY: "auto",
  },
  formCard: {
    width: "100%", maxWidth: 520,
    background: "white", borderRadius: 24,
    boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)",
    padding: "40px 40px",
    border: "1px solid #e2e8f0",
    margin: "24px 0",
  },
  formHeader: { textAlign: "center", marginBottom: 28 },
  formIconWrap: {
    width: 56, height: 56, borderRadius: 16, background: "#e0f2fe",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 14px",
  },
  formTitle: {
    fontWeight: 800, fontSize: "1.6rem", color: "#0f172a",
    margin: 0, marginBottom: 6, letterSpacing: "-0.02em",
  },
  formSub: { fontSize: "0.875rem", color: "#64748b", margin: 0 },
  errorBox: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 10, padding: "10px 14px",
    fontSize: "0.85rem", color: "#dc2626", marginBottom: 20,
  },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: "0.83rem", fontWeight: 600, color: "#374151" },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  inputIcon: {
    position: "absolute", left: 13, width: 17, height: 17,
    fill: "#94a3b8", pointerEvents: "none", flexShrink: 0,
  },
  input: {
    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 11,
    padding: "11px 13px 11px 40px", fontSize: "0.92rem",
    fontFamily: "'Afacad Flux', sans-serif", color: "#0f172a",
    outline: "none", background: "#f8fafc", boxSizing: "border-box",
  },
  eyeBtn: {
    position: "absolute", right: 10, background: "none",
    border: "none", cursor: "pointer", padding: 4,
    display: "flex", alignItems: "center",
  },
  roleRow: { display: "flex", gap: 10 },
  roleActive: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
    gap: 8, padding: "11px 12px", borderRadius: 11,
    background: "linear-gradient(135deg, #e0f2fe 0%, #ccfbf1 100%)",
    border: "2px solid #0ea5e9", color: "#0369a1",
    fontFamily: "'Afacad Flux', sans-serif", fontSize: "0.875rem",
    fontWeight: 700, cursor: "pointer",
  },
  roleInactive: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
    gap: 8, padding: "11px 12px", borderRadius: 11,
    background: "#f8fafc", border: "1.5px solid #e2e8f0",
    color: "#64748b", fontFamily: "'Afacad Flux', sans-serif",
    fontSize: "0.875rem", fontWeight: 600, cursor: "pointer",
  },
  btn: {
    marginTop: 4, padding: "13px", borderRadius: 12, background: grad,
    border: "none", color: "white", fontFamily: "'Afacad Flux', sans-serif",
    fontSize: "1rem", fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 16px rgba(14,165,233,0.4)", width: "100%",
  },
  btnDisabled: {
    marginTop: 4, padding: "13px", borderRadius: 12,
    background: "linear-gradient(135deg, #7dd3fc 0%, #5eead4 100%)",
    border: "none", color: "white", fontFamily: "'Afacad Flux', sans-serif",
    fontSize: "1rem", fontWeight: 700, cursor: "not-allowed", width: "100%",
  },
  btnInner: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
  spinner: {
    display: "inline-block", width: 16, height: 16,
    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
  },
  switchText: { textAlign: "center", fontSize: "0.875rem", color: "#64748b", marginTop: 20, marginBottom: 0 },
  switchLink: { color: "#0ea5e9", fontWeight: 700, textDecoration: "none" },
};
